// AgpOverview.logic.ts
import groupBy from 'lodash/groupBy';
import { Agp } from '../api/agp';
import { exciseDutyInfoNL } from '../data/exciseInfo';
import { Batch } from '../models/Batch';
import { Recipe } from '../models/Recipe';

export type Direction = 'IN' | 'OUT' | 'VOID';

export type Movement = {
  id: string;
  amount: number;
  sold: number;
  date: Date;
  direction: Direction;
  slug: string;
  bottleName: string;
  batchId: string;
  note?: string;
  size: number;
  excisePaidDate?: Date;
  excisePaymentReferenceId?: string;
};

export type ProductRow = {
  slug: string;
  bottleName: string;
  onHand: number;
  inTotal: number;
  voidedTotal: number;
  outTotal: number;
  batches: Array<{
    batchShort: string;
    batchId: string;
    onHand: number;
    depleted: boolean;
  }>;
};

export type ViewMode = 'month' | 'quarter' | 'year';

// --- Date helpers (UTC-safe, names unchanged) ---
export const toJsDate = (val: any): Date => (val?.toDate ? val.toDate() : new Date(val ?? Date.now()));

const startOfMonth = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));

const startOfYear = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

const addMonths = (d: Date, n: number) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));

const addQuarters = (d: Date, n: number) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n * 3, 1));

const addYears = (d: Date, n: number) => new Date(Date.UTC(d.getUTCFullYear() + n, d.getUTCMonth(), 1));

export const getPeriodBounds = (mode: 'month' | 'quarter' | 'year', date: Date) => {
  if (mode === 'month') {
    const from = startOfMonth(date);
    const to = addMonths(from, 1);
    return { from, to };
  }
  if (mode === 'quarter') {
    const from = new Date(Date.UTC(date.getUTCFullYear(), Math.floor(date.getUTCMonth() / 3) * 3, 1));
    const to = addQuarters(from, 1);
    return { from, to };
  }
  const from = startOfYear(date);
  const to = addYears(from, 1);
  return { from, to };
};

// --- Batch helpers ---
export const getSlugForBatch = (
  batchMap: Record<string, Batch>,
  recipeMap: Record<string, Recipe>,
  batchId: string,
): string => {
  const b = batchMap[batchId];
  if (!b) return 'unknown';
  return b.recipe?.slug ?? (b as any).recipeSlug ?? (b.recipeId ? recipeMap[b.recipeId]?.slug : undefined) ?? 'unknown';
};

export const getRatePer100LForBatch = (
  batchMap: Record<string, Batch>,
  recipeMap: Record<string, Recipe>,
  bid: string,
): number => {
  const batch = batchMap[bid];
  const recipe = batch?.recipeId ? recipeMap[batch.recipeId] : batch?.recipe;

  let abv = (batch?.abvFinal ?? 0) as number;
  if (!abv) abv = (recipe?.defaultAbv ?? 0) as number;
  if (abv === 0) return -99;

  const isSparkling = (batch as any)?.isSparkling ?? (recipe as any)?.isSparkling ?? false;
  const isSession = abv <= exciseDutyInfoNL.session.abvLimit;

  if (isSession) return exciseDutyInfoNL.session.ratePer100L;
  return isSparkling ? exciseDutyInfoNL.sparkling.ratePer100L : exciseDutyInfoNL.still.ratePer100L;
};

export const getBatchBalances = (movements: Movement[]) => {
  const byBatch = new Map<string, number>();
  for (const m of movements) {
    const delta = m.direction === 'IN' ? m.amount : -m.amount;
    byBatch.set(m.batchId, (byBatch.get(m.batchId) ?? 0) + delta);
  }
  return byBatch;
};

export const getDepletedBatchIds = (movements: Movement[]): Set<string> => {
  const balances = getBatchBalances(movements);
  const depleted = new Set<string>();
  balances.forEach((onHand, batchId) => {
    if (onHand <= 0) depleted.add(batchId);
  });
  return depleted;
};

// --- Transformations ---
export const mapAgpToMovements = (
  agp: Agp[],
  batchMap: Record<string, Batch>,
  recipeMap: Record<string, Recipe>,
): Movement[] =>
  agp
    .map((e) => {
      const baseAmount = e.amount ?? 0;
      const hasReleased = !!e.releasedDate;
      const hasVoided = !!e.voidedDate;

      let direction: Direction = hasReleased ? 'OUT' : hasVoided ? 'VOID' : 'IN';
      let amount = Math.abs(baseAmount);
      if (!hasReleased && baseAmount < 0) {
        direction = 'OUT';
        amount = Math.abs(baseAmount);
      }

      return {
        id: (e as any).id ?? Math.random().toString(36),
        amount,
        sold: e.sold ?? 0,
        date: toJsDate(e.createdDate),
        direction,
        slug: e.recipeSlug ?? getSlugForBatch(batchMap, recipeMap, e.batchId),
        bottleName: e.name ?? 'Unknown',
        batchId: e.batchId,
        size: e.size ?? 0,
        note: e.note,
        excisePaidDate: e.excisePaidDate ? toJsDate(e.excisePaidDate) : undefined,
        excisePaymentReferenceId: e.excisePaymentReferenceId,
      };
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime());

// Group by product and batch
export const groupMovementsByProduct = (movements: Movement[], batchMap: Record<string, Batch>): ProductRow[] =>
  Object.entries(groupBy(movements, (m) => `${m.slug}::${m.bottleName}`)).map<ProductRow>(([, mv]) => {
    const slug = mv[0].slug;
    const bottleName = mv[0].bottleName;
    const inTotal = mv.filter((x) => x.direction === 'IN').reduce((s, x) => s + x.amount, 0);
    const voidedTotal = mv.filter((x) => x.direction === 'VOID').reduce((s, x) => s + x.amount, 0);
    const outTotal = mv.filter((x) => x.direction === 'OUT').reduce((s, x) => s + x.amount, 0);
    const onHand = inTotal - outTotal;

    const byBatch = Object.entries(groupBy(mv, (m) => m.batchId)).map(([batchId, items]) => {
      const bIn = items.filter((x) => x.direction === 'IN').reduce((s, x) => s + x.amount, 0);
      const bOut = items.filter((x) => x.direction === 'OUT').reduce((s, x) => s + x.amount, 0);
      const balance = bIn - bOut;
      const short = batchMap[batchId]?.batchId ?? batchId.slice(-6);

      return { batchShort: short, batchId, onHand: balance, depleted: balance <= 0 };
    });

    return {
      slug,
      bottleName,
      onHand,
      voidedTotal,
      inTotal,
      outTotal,
      batches: byBatch.sort((a, b) => b.onHand - a.onHand),
    };
  });

// --- KPIs and Alerts ---
export const computeKpis = (productRows: ProductRow[], movements: Movement[]) => {
  const totalOnHand = productRows.reduce((s, r) => s + r.onHand, 0);
  const uniqueSkus = new Set(productRows.map((r) => `${r.slug}::${r.bottleName}`)).size;
  const now = Date.now();
  const last30 = movements.filter((m) => m.direction === 'OUT' && now - m.date.getTime() <= 30 * 864e5);
  const voided30 = movements
    .filter((m) => m.direction === 'VOID' && now - m.date.getTime() <= 30 * 864e5)
    .reduce((s, m) => s + m.amount, 0);
  const releases30 = last30.reduce((s, m) => s + m.amount, 0);

  return { totalOnHand, uniqueSkus, releases30, voided30 };
};

export const computeAlerts = (productRows: ProductRow[], movements: Movement[]) => {
  const now = Date.now();
  const negatives = productRows.filter((r) => r.onHand < 0);
  const tooOld = movements
    .filter((m) => m.direction === 'IN')
    .filter((m) => now - m.date.getTime() > 180 * 864e5)
    .map((m) => `${m.slug} – ${m.bottleName}`);
  return {
    negatives: negatives.map((r) => `${r.slug} – ${r.bottleName} (${r.onHand})`),
    staleStock: Array.from(new Set(tooOld)),
  };
};

// --- Excise ---
export const computeExciseForMovement = (
  m: { size: number; amount: number; batchId: string },
  batchMap: Record<string, Batch>,
  recipeMap: Record<string, Recipe>,
): number => {
  const liters = (m.size ?? 0) * (m.amount ?? 0);
  const ratePer100L = getRatePer100LForBatch(batchMap, recipeMap, m.batchId);
  if (ratePer100L === -99) return 0;
  return (liters * ratePer100L) / 100;
};

export const computeTotalExcise = (
  releases: Movement[],
  batchMap: Record<string, Batch>,
  recipeMap: Record<string, Recipe>,
): number => releases.reduce((sum, m) => sum + computeExciseForMovement(m, batchMap, recipeMap), 0);
